import React, { useState, useEffect } from "react";
import { testPasswordStrength } from "./utils";
import { Shield, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";

const TIPS = [
  { icon: "8+", text: "Use at least 8 characters" },
  { icon: "Aa", text: "Mix uppercase & lowercase letters" },
  { icon: "1#", text: "Include numbers and symbols (!@#$%)" },
  { icon: "✗",  text: "Avoid names, birthdays, or 'password'" },
  { icon: "🔑", text: "Use unique passwords per account" },
];

const STRENGTH_CONFIG = {
  Weak:   { label: "Weak",   color: "#ef4444", bg: "rgba(239,68,68,0.1)",    bar: 1, glow: "rgba(239,68,68,0.3)"    },
  Medium: { label: "Medium", color: "#f59e0b", bg: "rgba(245,158,11,0.1)",   bar: 2, glow: "rgba(245,158,11,0.3)"  },
  Strong: { label: "Strong", color: "#10b981", bg: "rgba(16,185,129,0.1)",   bar: 3, glow: "rgba(16,185,129,0.35)" },
};

const CRITERIA = [
  { label: "8+ characters",    check: (p: string) => p.length >= 8 },
  { label: "Uppercase letter", check: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase letter", check: (p: string) => /[a-z]/.test(p) },
  { label: "Number",           check: (p: string) => /\d/.test(p) },
  { label: "Symbol (!@#$%)",   check: (p: string) => /[!@#$%&*^]/.test(p) },
];

interface Props {
  onComplete?: (score: number, maxScore: number) => void;
}

export default function PasswordProtector({ onComplete }: Props): React.JSX.Element {
  const [password, setPassword]   = useState("");
  const [strength, setStrength]   = useState<"Weak" | "Medium" | "Strong">("Weak");
  const [visible, setVisible]     = useState(false);
  const [completed, setCompleted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPassword(val);
    setStrength(testPasswordStrength(val));
  };

  // Fire onComplete once when a Strong password is first achieved
  useEffect(() => {
    if (strength === "Strong" && !completed) {
      setCompleted(true);
      onComplete?.(50, 50); // full marks for creating a strong password
    }
  }, [strength, completed, onComplete]);

  const cfg = STRENGTH_CONFIG[strength];

  return (
    <div className="flex flex-col gap-5">

      {/* Tips */}
      <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, #f0f4ff, #e8edff)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Shield className="w-4 h-4 text-indigo-600" />
          </div>
          <p className="text-sm font-semibold text-indigo-800">Password Tips</p>
        </div>
        <div className="space-y-2">
          {TIPS.map(t => (
            <div key={t.text} className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 shrink-0">
                {t.icon}
              </span>
              <span className="text-xs text-slate-600">{t.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Input */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
          Try a password
        </label>
        <div className="relative">
          <input
            type={visible ? "text" : "password"}
            placeholder="Enter a password…"
            value={password}
            onChange={handleChange}
            className="w-full rounded-2xl px-4 py-3 pr-11 text-sm font-medium text-slate-700 outline-none transition-all"
            style={{
              background: '#f8fafc',
              border: password ? `2px solid ${cfg.color}` : '2px solid #e2e8f0',
              boxShadow: password ? `0 0 0 3px ${cfg.glow}` : 'none',
            }}
          />
          <button
            type="button"
            onClick={() => setVisible(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', padding: 0, lineHeight: 1 }}
          >
            {visible
              ? <EyeOff className="w-4 h-4 text-slate-400" />
              : <Eye    className="w-4 h-4 text-slate-400" />
            }
          </button>
        </div>
      </div>

      {/* Strength meter */}
      {password.length > 0 && (
        <>
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-slate-500">Strength</span>
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
                {cfg.label}
              </span>
            </div>
            <div className="flex gap-1.5">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="h-2 flex-1 rounded-full transition-all duration-300"
                  style={{ background: i <= cfg.bar ? cfg.color : '#e2e8f0' }}
                />
              ))}
            </div>
          </div>

          {/* Criteria checklist */}
          <div className="grid grid-cols-2 gap-2">
            {CRITERIA.map(c => {
              const ok = c.check(password);
              return (
                <div key={c.label} className="flex items-center gap-1.5">
                  {ok
                    ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    : <XCircle    className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  }
                  <span className={`text-xs ${ok ? 'text-slate-600 font-medium' : 'text-slate-400'}`}>
                    {c.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Strong congrats */}
          {strength === "Strong" && (
            <div className="rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(16,185,129,0.08)' }}>
              <span className="text-2xl">🎉</span>
              <div>
                <p className="text-sm font-bold text-emerald-700">Great password!</p>
                <p className="text-xs text-emerald-600 mt-0.5">This password is strong and hard to crack.</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
